import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link, Outlet } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';

// AIBL Logo
const LOGO = 'https://customer-assets.emergentagent.com/job_3c7ea79e-55a3-44e4-9827-1c3ee83e6f4f/artifacts/pc4k8dbt_Group%2083.png';

// Pixel Avatar for Sidebar (GitHub-style identicon)
function SidebarAvatar({ seed, size = 36 }) {
  // Generate deterministic pattern from seed
  let hash = 0;
  const str = seed || 'default';
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash = hash & hash;
  }
  
  const hue = Math.abs(hash % 360);
  const bgColor = `hsl(${hue}, 60%, 45%)`;
  const fgColor = `hsl(${(hue + 180) % 360}, 60%, 85%)`;
  
  const pattern = [];
  for (let row = 0; row < 5; row++) {
    const rowPattern = [];
    for (let col = 0; col < 3; col++) {
      rowPattern.push((Math.abs(hash >> (row * 3 + col)) & 1) === 1);
    }
    pattern.push([...rowPattern, rowPattern[1], rowPattern[0]]);
  }
  
  return (
    <div 
      className="rounded-full overflow-hidden flex-shrink-0"
      style={{ width: size, height: size, backgroundColor: bgColor }}
    >
      <svg width={size} height={size} viewBox="0 0 5 5">
        {pattern.map((row, y) =>
          row.map((cell, x) =>
            cell && <rect key={`${x}-${y}`} x={x} y={y} width={1} height={1} fill={fgColor} />
          )
        )}
      </svg>
    </div>
  );
}

// Custom Icons
export const Icons = {
  Agents: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="8" r="4"/>
      <path d="M4 20c0-4 4-6 8-6s8 2 8 6"/>
      <circle cx="19" cy="8" r="2"/>
      <circle cx="5" cy="8" r="2"/>
    </svg>
  ),
  Wallets: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="6" width="20" height="14" rx="2"/>
      <path d="M16 14a2 2 0 100-4 2 2 0 000 4z" fill="currentColor"/>
      <path d="M2 10h20"/>
    </svg>
  ),
  Plugins: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="3" width="7" height="7" rx="1"/>
      <rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="3" y="14" width="7" height="7" rx="1"/>
      <rect x="14" y="14" width="7" height="7" rx="1"/>
    </svg>
  ),
  Transactions: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M7 10l-3 3 3 3"/>
      <path d="M4 13h13"/>
      <path d="M17 14l3-3-3-3"/>
      <path d="M20 11H7"/>
    </svg>
  ),
  Enaibl: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="9"/>
      <path d="M12 6v6l4 2"/>
      <path d="M8 15l4-3 4 3"/>
    </svg>
  ),
  Settings: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="3"/>
      <path d="M12 1v4m0 14v4m11-11h-4M5 12H1m17.36-6.36l-2.83 2.83M9.47 14.53l-2.83 2.83m11.72 0l-2.83-2.83M9.47 9.47L6.64 6.64"/>
    </svg>
  ),
  Docs: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M4 4h16v16H4z"/>
      <path d="M8 8h8m-8 4h8m-8 4h4"/>
    </svg>
  ),
  ApiKey: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.78 7.78 5.5 5.5 0 017.78-7.78zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
    </svg>
  ),
  Menu: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 6h18M3 12h18M3 18h18"/>
    </svg>
  ),
  Close: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M18 6L6 18M6 6l12 12"/>
    </svg>
  ),
  Search: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="11" cy="11" r="8"/>
      <path d="M21 21l-4.35-4.35"/>
    </svg>
  ),
  Bell: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M18 8A6 6 0 106 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 01-3.46 0"/>
    </svg>
  ),
  Logout: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
      <polyline points="16 17 21 12 16 7"/>
      <line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
  ChevronDown: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M6 9l6 6 6-6"/>
    </svg>
  ),
  External: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
      <polyline points="15 3 21 3 21 9"/>
      <line x1="10" y1="14" x2="21" y2="3"/>
    </svg>
  ),
  Plus: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 5v14M5 12h14"/>
    </svg>
  ),
  Copy: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="9" y="9" width="13" height="13" rx="2"/>
      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
    </svg>
  ),
  Swap: ({ size = 20, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
      <path d="M7 16V4m0 0L3 8m4-4l4 4"/>
      <path d="M17 8v12m0 0l4-4m-4 4l-4-4"/>
    </svg>
  ),
  Transfer: ({ size = 20, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
      <path d="M5 12h14M12 5l7 7-7 7"/>
    </svg>
  ),
  Wrap: ({ size = 20, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
      <circle cx="12" cy="12" r="9"/>
      <path d="M12 8v8m-4-4h8"/>
    </svg>
  ),
  Buy: ({ size = 20, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
      <path d="M12 2v20M2 12h20"/>
      <circle cx="12" cy="12" r="4"/>
    </svg>
  ),
  Bridge: ({ size = 20, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
      <path d="M4 15l4-8 4 8 4-8 4 8"/>
      <path d="M4 19h16"/>
    </svg>
  ),
  Trophy: ({ size = 20, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
      <path d="M6 9H4.5a2.5 2.5 0 010-5H6"/>
      <path d="M18 9h1.5a2.5 2.5 0 000-5H18"/>
      <path d="M4 22h16"/>
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
      <path d="M18 2H6v7a6 6 0 0012 0V2z"/>
    </svg>
  ),
  Account: ({ size = 20, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
      <circle cx="12" cy="8" r="4"/>
      <path d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2"/>
    </svg>
  ),
  Safe: ({ size = 20, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
      <rect x="3" y="11" width="18" height="11" rx="2"/>
      <path d="M7 11V7a5 5 0 0110 0v4"/>
      <circle cx="12" cy="16" r="1"/>
    </svg>
  ),
  Shield: ({ size = 20, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      <path d="M9 12l2 2 4-4"/>
    </svg>
  ),
};

// Nav items - Full navigation per PRD
const NAV_ITEMS = [
  { id: 'agents', label: 'Dashboard', icon: Icons.Agents, path: '/dashboard' },
  { id: 'wallets', label: 'Wallets', icon: Icons.Wallets, path: '/dashboard/wallets' },
  { id: 'plugins', label: 'Plugins', icon: Icons.Plugins, path: '/dashboard/plugins' },
  { id: 'multisig', label: 'Multi-Sig', icon: Icons.Safe, path: '/dashboard/multisig' },
  { id: 'safesign', label: 'Sign Transactions', icon: Icons.Shield, path: '/dashboard/safesign' },
  { id: 'transactions', label: 'Transactions', icon: Icons.Transactions, path: '/dashboard/transactions' },
  { id: 'leaderboard', label: 'Leaderboard', icon: Icons.Trophy, path: '/dashboard/leaderboard' },
];

const BOTTOM_ITEMS = [
  { id: 'account', label: 'Account', icon: Icons.Account, path: '/dashboard/account' },
  { id: 'docs', label: 'Docs', icon: Icons.Docs, href: 'https://docs.aibl.money', external: true },
];

export default function DashboardLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, loading, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, loading, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  const getPageTitle = () => {
    const item = NAV_ITEMS.find(i => isActive(i.path));
    return item?.label || 'Dashboard';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <motion.div 
          className="w-10 h-10 border-2 border-white/10 border-t-white/60 rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        />
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return (
    <div className="h-screen bg-black text-white flex overflow-hidden">
      {/* Mobile overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar - Fixed full height */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 flex flex-col h-screen bg-[#050508] border-r border-white/[0.06] transition-all duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } ${sidebarCollapsed ? 'w-[72px]' : 'w-[260px]'}`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-5 border-b border-white/[0.06]">
          <Link to="/" className={sidebarCollapsed ? 'hidden' : 'block'}>
            <img src={LOGO} alt="AIBL" className="h-6" />
          </Link>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-white/40 hover:text-white"
          >
            <Icons.Close size={20} />
          </button>
          <button 
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden lg:block text-white/40 hover:text-white p-1"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {sidebarCollapsed ? <path d="M9 18l6-6-6-6"/> : <path d="M15 18l-6-6 6-6"/>}
            </svg>
          </button>
        </div>

        {/* User card */}
        {!sidebarCollapsed && (
          <div className="p-4 border-b border-white/[0.06]">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] hover:bg-white/[0.04] transition-colors cursor-pointer">
              <SidebarAvatar seed={user?.walletAddress || user?.email || user?.displayName || user?.name || 'default'} />
              <div className="flex-1 min-w-0">
                <p className="text-white/90 text-sm font-medium truncate">
                  {user?.displayName || user?.name || 'Developer'}
                </p>
                <p className="text-white/40 text-xs truncate">
                  {user?.email || user?.wallet_address?.slice(0, 10) + '...'}
                </p>
              </div>
              <Icons.ChevronDown size={14} className="text-white/30" />
            </div>
          </div>
        )}

        {/* Navigation - Scrollable middle section */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto min-h-0">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.id}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  active 
                    ? 'bg-white/[0.08] text-white' 
                    : 'text-white/50 hover:text-white/80 hover:bg-white/[0.03]'
                }`}
                title={sidebarCollapsed ? item.label : undefined}
              >
                <Icon size={18} />
                {!sidebarCollapsed && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Bottom nav - Fixed at bottom */}
        <div className="flex-shrink-0 p-3 space-y-1 border-t border-white/[0.06]">
          {BOTTOM_ITEMS.map((item) => {
            const Icon = item.icon;
            if (item.external) {
              return (
                <a
                  key={item.id}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-white/50 hover:text-white/80 hover:bg-white/[0.03] transition-all"
                  title={sidebarCollapsed ? item.label : undefined}
                >
                  <Icon size={18} />
                  {!sidebarCollapsed && (
                    <>
                      <span>{item.label}</span>
                      <Icons.External size={12} className="ml-auto text-white/30" />
                    </>
                  )}
                </a>
              );
            }
            return (
              <Link
                key={item.id}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  isActive(item.path)
                    ? 'bg-white/[0.08] text-white'
                    : 'text-white/50 hover:text-white/80 hover:bg-white/[0.03]'
                }`}
                title={sidebarCollapsed ? item.label : undefined}
              >
                <Icon size={18} />
                {!sidebarCollapsed && <span>{item.label}</span>}
              </Link>
            );
          })}
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-400/70 hover:text-red-400 hover:bg-red-500/10 transition-all"
            title={sidebarCollapsed ? 'Sign Out' : undefined}
          >
            <Icons.Logout size={18} />
            {!sidebarCollapsed && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* Main content - Scrollable */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        {/* Top bar - Fixed */}
        <header className="flex-shrink-0 h-16 flex items-center justify-between px-4 lg:px-6 border-b border-white/[0.06] bg-black/50 backdrop-blur-xl z-30">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-white/60 hover:text-white"
            >
              <Icons.Menu size={22} />
            </button>
            <h1 className="text-lg font-semibold text-white">{getPageTitle()}</h1>
          </div>

          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="hidden md:flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.03] border border-white/[0.06] w-[240px]">
              <Icons.Search size={16} className="text-white/30" />
              <input
                type="text"
                placeholder="Search agents, transactions..."
                className="bg-transparent text-sm text-white placeholder:text-white/30 outline-none flex-1"
              />
            </div>
            
            {/* Notifications */}
            <button className="p-2.5 rounded-xl bg-white/[0.03] border border-white/[0.06] text-white/50 hover:text-white hover:border-white/10 transition-all relative">
              <Icons.Bell size={18} />
              <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full" />
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6 overflow-y-auto">
          {children}
        </main>
      </div>

      {/* Custom scrollbar styles */}
      <style>{`
        ::-webkit-scrollbar {
          width: 6px;
        }
        ::-webkit-scrollbar-track {
          background: transparent;
        }
        ::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.15);
          border-radius: 3px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: rgba(255,255,255,0.25);
        }
      `}</style>
    </div>
  );
}
